import React, { useState } from 'react';
import { Button } from 'rebass';
import { FiCheck, FiEdit, FiX } from 'react-icons/fi';
import classNames from 'classnames';
import { renderBoolean } from 'components/ProtectedField';
import { Select } from '@rebass/forms';
import { useUpdateIsOptInConfirmedMutation } from 'api/useUpdateIsOptInConfirmedMutation';
import { useToastHelper } from 'hooks/useToastHelper';
import services from 'services/services';

/**
 * @typedef {Object} OptInConfirmedActionProps
 * @property {Object} person
 * @property {boolean} person.is_opt_in_confirmed
 * @property {number} person.id
 * @property {(value: boolean) => void} [onUpdate]
 * @property {string} [className]
 * @property {React.CSSProperties} [style]
 */

/**
 * Renders an Opt-In Confirmed action component that allows editing and updating the opt-in confirmed status of a person.
 *
 * @param {OptInConfirmedActionProps} props
 * @returns {React.ReactNode}
 */
export function OptInConfirmedAction({ person, className, style, onUpdate }) {
    const { addSuccessToast, addErrorToast } = useToastHelper();
    const [isEditMode, setIsEditMode] = useState(false);
    const [value, setValue] = useState(person.is_opt_in_confirmed);
    const { mutate: updateIsOptInConfirmed } = useUpdateIsOptInConfirmedMutation({
        onSuccess: (_, payload) => {
            cancel();
            onUpdate?.(payload.value);
            addSuccessToast('Opt-in confirmed status updated');
        },
        onError: error => {
            const message = services.parseAndTrackXhrErrors(error);
            addErrorToast(message);
        }
    });

    const cancel = () => {
        setIsEditMode(false);
    };

    const submit = () => {
        updateIsOptInConfirmed({
            personId: person.id,
            value
        });
    };

    const activateEditMode = () => {
        setIsEditMode(true);
    };

    return (
        <div
            className={classNames(
                'flex nowrap justify-between items-center border-line-light border-0 border-solid color-text-primary hover:bg-[#fbfdff]',
                className
            )}
            style={{ minHeight: '52px', ...style }}
        >
            <div style={{ width: 'calc(5 / 16 * 100%)' }} className="medium">
                Opt In Confirmed
            </div>
            <div className="grow">
                {isEditMode ? (
                    <Select value={value} onChange={event => setValue(+event.target.value)}>
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                    </Select>
                ) : (
                    renderBoolean(person.is_opt_in_confirmed)
                )}
            </div>
            <div style={{ width: '25%' }} className="text-right">
                {isEditMode ? (
                    <>
                        <Button variant="transparent" onClick={cancel}>
                            <FiX />
                            Cancel
                        </Button>
                        <Button variant="primary-transparent" className="!ml-1" onClick={submit}>
                            <FiCheck />
                            Save
                        </Button>
                    </>
                ) : (
                    <Button variant="transparent" onClick={activateEditMode}>
                        <FiEdit />
                        Edit
                    </Button>
                )}
            </div>
        </div>
    );
}
